import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc,
  orderBy,
  query,
  onSnapshot 
} from 'firebase/firestore';
import { db } from '../../config/firebase';

class TemplatesFirestoreService {
  getUserTemplatesRef(userId) {
    return collection(db, 'userTemplates', userId, 'templates');
  }

  getUserTemplateRef(userId, templateName) {
    return doc(db, 'userTemplates', userId, 'templates', templateName);
  }

  async createTemplate(userId, templateData) {
    const {
      displayName,
      headerType,
      headerText,
      headerMediaUrl,
      machineName,
      type,
      textRaw,
      textWithVariables,
      variablesMapping,
      variables,  // Array of variable definitions
      cta,
      buttons,  // New: support buttons array
      twilioContentSid,
      twilioApprovalSid,
      category
    } = templateData;

    const templateDoc = {
      id: machineName,
      displayName,
      headerType: headerType || 'text',
      headerText: headerText || null,
      headerMediaUrl: headerMediaUrl || null,
      machineName,
      type,
      textRaw,
      textWithVariables,
      variablesMapping,
      variables: variables || [],  // Store variable definitions
      cta: cta || null,
      buttons: buttons || [],  // Store buttons array
      category: category || 'MARKETING',
      twilioContentSid,
      twilioApprovalSid: twilioApprovalSid || null,
      status: 'pending',
      rejectionReason: null,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    try {
      const templateRef = this.getUserTemplateRef(userId, machineName);
      await setDoc(templateRef, templateDoc);
      return templateDoc;
    } catch (error) {
      console.error('Failed to create template in Firestore:', error);
      throw error;
    }
  }

  async updateTemplateStatus(userId, templateName, status, rejectionReason = null) {
    try {
      const templateRef = this.getUserTemplateRef(userId, templateName);
      await updateDoc(templateRef, {
        status,
        rejectionReason,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Failed to update template status:', error);
      throw error;
    }
  }

  async updateTemplate(userId, templateName, updates) {
    try {
      const templateRef = this.getUserTemplateRef(userId, templateName);
      await updateDoc(templateRef, {
        ...updates,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Failed to update template:', error);
      throw error;
    }
  }

  async getTemplate(userId, templateName) {
    try {
      const templateRef = this.getUserTemplateRef(userId, templateName);
      const doc = await getDoc(templateRef);
      
      if (doc.exists()) {
        return { id: doc.id, ...doc.data() };
      }
      return null;
    } catch (error) {
      console.error('Failed to get template:', error);
      throw error;
    }
  }

  async getUserTemplates(userId) {
    try {
      const templatesRef = this.getUserTemplatesRef(userId);
      const q = query(templatesRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Failed to get user templates:', error);
      throw error;
    }
  }

  async deleteTemplate(userId, templateName) {
    try {
      const templateRef = this.getUserTemplateRef(userId, templateName);
      await deleteDoc(templateRef);
    } catch (error) {
      console.error('Failed to delete template:', error);
      throw error;
    }
  }

  // Real-time listener for template updates
  subscribeToUserTemplates(userId, callback) {
    const templatesRef = this.getUserTemplatesRef(userId);
    const q = query(templatesRef, orderBy('createdAt', 'desc'));
    
    return onSnapshot(q, (snapshot) => {
      const templates = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      callback(templates);
    }, (error) => {
      console.error('Error in templates subscription:', error);
    });
  }
}

export default new TemplatesFirestoreService();